import {
	type AppState,
	Auth0Provider,
	type User as Auth0User,
	type GetTokenSilentlyOptions,
	type LogoutOptions,
	MissingRefreshTokenError,
	type RedirectLoginOptions,
	TimeoutError,
	useAuth0,
} from "@auth0/auth0-react";
import { isEmpty } from "es-toolkit/compat";
import { useRouter } from "next/router";
import { type FC, type PropsWithChildren, useEffect, useState } from "react";

import { generateShortUuid } from "@moment/api-collab";
import { Logger } from "@moment/logging";
import {
	$documentToken,
	env,
	getValidDocumentToken,
	store,
	useAuth,
	useAuthStatus,
	useRouteParams,
} from "@moment/state";
import { invokePostMessage, isRunningInIframe, makeToast } from "@moment/ui";

import { RustAuthListener } from "./RustAuthListener";
import { getAuthorizationParams } from "./interop/common";

const logger = new Logger("AuthProvider");

const AuthProviderComponent: FC<PropsWithChildren> = ({ children }) => {
	return (
		<Auth0Provider
			domain={env.authSettings.auth0Domain}
			clientId={env.authSettings.auth0ClientId}
			authorizationParams={getAuthorizationParams()}
			cacheLocation="localstorage"
			useRefreshTokens={true}
		>
			<AccessTokenInjector />
			<RustAuthListener />
			{children}
		</Auth0Provider>
	);
};

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => (
	<AuthProviderComponent>{children}</AuthProviderComponent>
);

export type GetAccessTokenSilently = (opts?: {
	promptForLogin?: boolean;
	redirectPath?: string;
}) => Promise<string | undefined>;

/**
 * Response type for auth token requests from iframe to parent.
 * This allows the iframe to properly cache "no auth required" state
 * vs "auth still loading" state.
 */
export type AuthTokenResponse =
	| { status: "authenticated"; access_token: string; expires_in: number }
	| { status: "unauthenticated" } // User not logged in, cacheable
	| { status: "pending" }; // Auth still loading, should retry

const iframeAuth0 = {
	loginWithRedirect: async (options?: RedirectLoginOptions<AppState>) => {
		// We don't need the redirect entry in the iframe.
		await invokePostMessage<void>("loginWithRedirect", {
			options: options,
		});
	},
	getAuth0User: async () => await invokePostMessage<Auth0User>("getAuth0User"),
	isAuthenticated: async () => await invokePostMessage<boolean>("isAuthenticated"),
	logout: async (options?: LogoutOptions) => {
		// TODO: pass logout options or remove the parameter entirely
		await invokePostMessage<void>("logoutWithRedirect", {
			options: options,
		});
	},
};

const AccessTokenInjector = () => {
	const {
		getAccessTokenSilently,
		logout,
		isLoading,
		isAuthenticated,
		user,
		error: authError,
	} = useAuth0();
	const [parentFrameIsAuthenticated, setParentFrameIsAuthenticated] = useState<boolean>(false);
	const [parentFrameUser, setParentFrameUser] = useState<Auth0User>();
	const { query } = useRouter();
	const { setIsAuthenticated, setLoggedInUser, setGetAccessTokenSilently } = useAuth();
	const { authStatusRef, setAuthStatus } = useAuthStatus();
	const authorizationParams = getAuthorizationParams();

	const router = useRouter();

	// Watch for postlogin/postlogout failure.
	useEffect(() => {
		const { postlogin, postlogout } = router.query;
		if (postlogin === "true" || postlogout === "true") {
			if (authError) {
				logger.error("Authentication error after login redirect", {
					authError,
					path: router.query,
				});
				makeToast({
					message:
						"There was an error with your login. Please try again or contact support@moment.dev",
					timer: "dismissible",
					variant: "error",
				});
				setAuthStatus("logged-out");
			}
		}
	}, [authError, router, setAuthStatus]);

	useEffect(() => {
		if (isLoading) {
			return undefined;
		}
		const traceId = "AuthProvider-" + generateShortUuid();
		const getAccessTokenSilentlyWrapper = async () => {
			// If we're in an iframe on web, request token
			// from parent via `postMessage`.
			// This handles both temporary tokens
			// (password-protected docs) and Auth0 tokens.
			if (env.isWebApp && isRunningInIframe()) {
				const response = await invokePostMessage<
					{ access_token: string; expires_in: number } | undefined
				>("getAuthToken");

				return response?.access_token;
			}

			// If we are still loading from auth0, return empty.
			if (isLoading) {
				return undefined;
			}

			try {
				const cachedResponse = await getAccessTokenSilently({
					cacheMode: "cache-only",
					detailedResponse: true,
					authorizationParams,
				} as GetTokenSilentlyOptions & { detailedResponse: true });
				if (cachedResponse) {
					if (authStatusRef.current !== "logged-in") {
						setAuthStatus("logged-in");
					}
					return cachedResponse.access_token;
				}
			} catch (err) {
				// Best-effort. Ignore.
			}

			try {
				const resp = await getAccessTokenSilently({
					cacheMode: "on",
					detailedResponse: true,
					authorizationParams,
				} as GetTokenSilentlyOptions & { detailedResponse: true });

				const token = resp?.access_token ?? "";
				if (!isEmpty(token)) {
					setAuthStatus("logged-in");
				} else if (authStatusRef.current === "logged-in") {
					// If we did not get a token, we are logged out
					// but only if the auth status has not been changed
					// already due to error handling potentially from
					// a previous call.
					setAuthStatus("logged-out-forced");
				}
				return token;
			} catch (error) {
				if (error instanceof Error) {
					// As of Sept 2025, Auth0 requires browsers to rotate refresh tokens on every use.
					// This means if a refresh token has been used to create another token,
					// you cannot use that token again.
					// Ideally, this is pretty rare, but can happen if the refresh token flow errors out.
					// If a user is logged in and this happens, we need to ask the user to log in again.
					//
					// reading the source code, it looks like auth0-spa-js assigns the error_message to the message field.
					// https://github.com/auth0/auth0-spa-js/blob/d57e52dfcc9fe6a35a9c357695b7ea627c9b7fc2/src/http.ts#L187-L225
					//
					// Below is what the auth0 server returns for an invalid refresh token:
					// response payload {
					// 		"error": "invalid_grant",
					// 		"error_description": "Unknown or invalid refresh token."
					// 	}
					const errorMessage = error.message?.toLowerCase();

					// If we get this error, this means we tried to use the refresh token twice.
					// No matter what, call the login flow, as there is no other way to get another refresh token.
					if (errorMessage?.includes("unknown or invalid refresh token.")) {
						logger.error(
							`[${traceId}] [AuthTokenAtom] Refresh token is invalid. This is bad. this means we tried to use a refresh token twice.`,
							{ error, authStatus: authStatusRef.current, isLoading }
						);

						// If we get this error on the first call (after page load),
						// authStatus will be "initializing".
						// We still want to trigger the login flow
						// if that is the case because if a refresh
						// token exists but is invalid,
						// this means the user was once
						// logged-in before.
						//
						// BUT we do not want to trigger the login
						// window multiple times, so we check that
						// authStatus is not "logging-in" already.
						if (authStatusRef.current !== "logging-in" && !isLoading) {
							// we need to log out to clear the invalid refresh token.
							await logout({ openUrl: false });
							setAuthStatus("logged-out-forced");
						}
						return;
					}
					// this could happen on startup or if the user manually cleared storage.
					if (
						error instanceof MissingRefreshTokenError ||
						// This might not seem necessary, but sometimes
						// the error message would come back without the
						// `MissingRefreshTokenError` type.
						errorMessage?.includes("missing refresh token")
					) {
						logger.warn(`[${traceId}] [AuthTokenAtom] Refresh token is missing.`, {
							error,
							authStatus: authStatusRef.current,
							isLoading,
						});

						// We only want to display the login popup
						// if the user was logged in before and
						// then we failed to get a refresh token.
						//
						// When the user is logging out,
						// the refresh token gets cleared,
						// and this error could happen if we
						// call getAccessTokenSilently during
						// the logout process.
						if (authStatusRef.current === "logged-in" && !isLoading) {
							logger.info(
								`[${traceId}] [AuthTokenAtom] User was in logged-in state but refresh token is missing or invalid. Triggering login flow.`,
								{
									error,
								}
							);
							setAuthStatus("logged-out-forced");
						}
						return;
					}

					if (error instanceof TimeoutError) {
						logger.error(
							`[${traceId}] [AuthTokenAtom] Timeout error! This means that the iframe in auth0 sdk did not complete. This could be a data loss issue!`,
							{
								error,
							}
						);
					}

					logger.error(
						`[${traceId}] [AuthTokenAtom] got error when trying to get access token silently.`,
						{
							error,
							authStatus: authStatusRef.current,
							isLoading,
						}
					);
					return undefined;
				}

				logger.error(
					`[${traceId}] [AuthTokenAtom] Unknown error getting access token silently:`,
					{
						error,
					}
				);

				throw error;
			}
		};
		setGetAccessTokenSilently({
			getAccessTokenSilently: getAccessTokenSilentlyWrapper,
			traceId,
		});
		// *********** IMPORTANT ***********
		// In order to prevent data loss, reboot only generates the context ONCE on load time of a web page.
		// Therefore, we can only set getAccessTokenSilently once!
		// It is imperative that we set the getAccessTokenSilently after auth0 is loaded.
		// Reading the "@auth0/auth0-react" source code, getAccessTokenSilently should never change,
		// it is a useCallback that listen to the wrapper client,
		// which gets instantiated once.
		// See https://github.com/auth0/auth0-react/blob/0091abb75dfbe6d070ddb5c82b16402753585f35/src/auth0-provider.tsx#L231-L248
		//
		// In summary, DO NOT add new dependencies to this array.
	}, [getAccessTokenSilently, isLoading]);

	useEffect(() => {
		setIsAuthenticated(isAuthenticated);
		setLoggedInUser(user);
	}, [user, isAuthenticated, setIsAuthenticated, setLoggedInUser]);

	// Get route params for temporary token lookup (parent-side only)
	const { workspaceName, documentId } = useRouteParams();

	// Listen for getAuthToken requests from iframe (parent only).
	// This handles both temporary tokens (password-protected docs) and Auth0 tokens.
	useEffect(() => {
		// Only run on web platform when NOT in an iframe (i.e., we are the parent)
		if (!env.isWebApp || isRunningInIframe()) return;

		const handleGetAuthToken = async (event: MessageEvent) => {
			if (event.data?.type !== "getAuthToken") return;

			const port = event.ports[0];
			if (!port) {
				console.error("getAuthToken requires a message port");
				return;
			}

			// 1. Check for temporary token (password-protected docs)
			if (workspaceName && documentId) {
				const tokenAtom = $documentToken({ workspaceId: workspaceName, documentId });
				const tempToken = store.get(tokenAtom);

				if (tempToken) {
					// Use getValidDocumentToken to refresh if needed
					const result = await getValidDocumentToken(tempToken, (newToken) => {
						store.set(tokenAtom, newToken);
					});

					if (result.success) {
						// Get fresh token after potential refresh
						const refreshedToken = store.get(tokenAtom);
						const expiresIn = refreshedToken
							? Math.max(
									0,
									Math.floor((refreshedToken.expiresAt - Date.now()) / 1000)
								)
							: 0;
						port.postMessage({
							status: "authenticated",
							access_token: result.accessToken,
							expires_in: expiresIn,
						} satisfies AuthTokenResponse);
						port.close();
						return;
					}
					// If refresh failed, fall through to try Auth0
				}
			}

			// 2. Check if Auth0 is still loading - iframe should retry
			if (isLoading) {
				port.postMessage({ status: "pending" } satisfies AuthTokenResponse);
				port.close();
				return;
			}

			// 3. Fall back to Auth0 token
			// Only attempt to get Auth0 token if user is authenticated
			if (!isAuthenticated) {
				// User not logged in - expected for public documents
				// Using typed response so iframe can cache this state
				port.postMessage({ status: "unauthenticated" } satisfies AuthTokenResponse);
				port.close();
				return;
			}

			try {
				const resp = await getAccessTokenSilently({
					cacheMode: "on",
					authorizationParams: getAuthorizationParams(),
				});
				if (resp) {
					// Auth0 tokens typically expire in 1 hour
					port.postMessage({
						status: "authenticated",
						access_token: resp,
						expires_in: 3600,
					} satisfies AuthTokenResponse);
				} else {
					port.postMessage({ status: "unauthenticated" } satisfies AuthTokenResponse);
				}
			} catch (error) {
				logger.error("Failed to get Auth0 token for iframe:", { error });
				port.postMessage({ status: "unauthenticated" } satisfies AuthTokenResponse);
			}
			port.close();
		};

		window.addEventListener("message", handleGetAuthToken);
		return () => window.removeEventListener("message", handleGetAuthToken);
	}, [workspaceName, documentId, getAccessTokenSilently, isAuthenticated, isLoading]);

	useEffect(() => {
		// We never want to run this hook for the desktop app.
		if (env.isDesktopApp || env.isMobileApp) {
			return;
		}

		// For the web platform, don't set the iframe
		// hooks in the auth actor if the app is not
		// not in an iframe.
		if (!isRunningInIframe()) {
			return;
		}

		if (!parentFrameIsAuthenticated) {
			iframeAuth0
				.isAuthenticated()
				.then(async (v: boolean | undefined) => {
					setParentFrameIsAuthenticated(v || false);
				})
				.catch(console.error);
		}

		if (!parentFrameUser) {
			iframeAuth0
				.getAuth0User()
				.then((u) => setParentFrameUser(u))
				.catch(console.error);
		}
	}, [parentFrameIsAuthenticated, parentFrameUser, query]);

	// Auth state is now managed via Jotai atoms (setIsAuthenticated, setLoggedInUser, setGetAccessTokenSilently)
	// No need to send to xstate machine anymore

	return null;
};
